import { useEffect } from 'react';
import { clsx } from 'clsx';
import { useNotificationStore } from '@store/notificationStore';
import { CheckCircle, AlertCircle, XCircle, Info, X } from 'lucide-react';

const ToastIcon = ({ type }: { type: 'success' | 'error' | 'warning' | 'info' }) => {
  switch (type) {
    case 'success':
      return <CheckCircle className="h-5 w-5 text-emerald-600" />;
    case 'error':
      return <XCircle className="h-5 w-5 text-red-600" />;
    case 'warning':
      return <AlertCircle className="h-5 w-5 text-amber-600" />;
    case 'info':
      return <Info className="h-5 w-5 text-content-secondary" />;
  }
};

const Toast = ({
  id,
  message,
  type,
}: {
  id: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
}) => {
  const { removeNotification } = useNotificationStore();

  useEffect(() => {
    const timer = setTimeout(() => {
      removeNotification(id);
    }, 3000);

    return () => clearTimeout(timer);
  }, [id, removeNotification]);

  const bgClasses = {
    success: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800',
    error: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
    warning: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
    info: 'bg-surface-raised border-border-default',
  };

  const textClasses = {
    success: 'text-emerald-800 dark:text-emerald-200',
    error: 'text-red-800 dark:text-red-200',
    warning: 'text-amber-800 dark:text-amber-200',
    info: 'text-content-primary',
  };

  return (
    <div
      role="alert"
      data-testid="notification"
      className={clsx(
        'pointer-events-auto flex items-center gap-3 px-4 py-3 rounded-lg border shadow-md animate-in slide-in-from-top fade-in',
        bgClasses[type]
      )}
    >
      <ToastIcon type={type} />
      <span className={clsx('flex-1', textClasses[type])}>{message}</span>
      <button
        onClick={() => removeNotification(id)}
        className={clsx('p-1 hover:opacity-70 transition-opacity', textClasses[type])}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export const ToastContainer = () => {
  const { notifications } = useNotificationStore();

  return (
    <div className="pointer-events-none fixed top-4 right-4 z-[100] max-w-md space-y-2">
      {notifications.map((notification) => (
        <Toast
          key={notification.id}
          id={notification.id}
          message={notification.message}
          type={notification.type}
        />
      ))}
    </div>
  );
};
